import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, Globe, Store } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthStore } from '@/features/auth/auth.store';
import { useBusinesses } from '@/features/business/use-businesses';
import { cn } from '@/lib/utils';


/**
 * Store selector.
 *
 * Sat at the top of the old sidebar as a bare blue button that showed only the
 * store name — with no indication that "Global" was a selectable state rather
 * than a loading placeholder, and no way to tell an admin viewing everything
 * from a manager locked to one store.
 */
export function BusinessSwitcher({ collapsed }: { collapsed: boolean }) {
  const { data: businesses, isPending, isError } = useBusinesses();
  const { t } = useTranslation();
  const business = useAuthStore((s) => s.business);
  const setBusiness = useAuthStore((s) => s.setBusiness);
  const canGoGlobal = useAuthStore(
    (s) => s.can('isAdmin') || s.can('isCustomerService')
  );

  if (isPending) {
    return (
      <div className={cn('p-3', collapsed && 'p-2')}>
        <Skeleton className={cn('h-11 w-full rounded-lg')} />
      </div>
    );
  }

  const label = business?.name ?? t('businessSwitcher.global');
  const options = businesses ?? [];

  return (
    <div className={cn('p-3', collapsed && 'p-2')}>
      <DropdownMenu>
        <DropdownMenuTrigger
          className={cn(
            'group flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors',
            'bg-background/60 hover:bg-sidebar-accent focus-visible:ring-sidebar-ring/50 outline-none focus-visible:ring-2',
            collapsed && 'justify-center px-0'
          )}
          aria-label={t('businessSwitcher.ariaLabel', { context: label })}
        >
          <span
            className={cn(
              'flex size-7 shrink-0 items-center justify-center rounded-md',
              business
                ? 'bg-sidebar-primary/12 text-sidebar-primary'
                : 'bg-muted text-muted-foreground'
            )}
          >
            {business ? (
              <Store className="size-3.5" />
            ) : (
              <Globe className="size-3.5" />
            )}
          </span>
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1">
                <span className="text-sidebar-foreground/50 block text-[0.65rem] font-medium tracking-wider uppercase">
                  {business
                    ? t('businessSwitcher.store')
                    : t('businessSwitcher.viewing')}
                </span>
                <span className="text-sidebar-foreground block truncate text-sm font-semibold">
                  {label}
                </span>
              </span>
              <ChevronsUpDown className="text-sidebar-foreground/40 size-4 shrink-0" />
            </>
          )}
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="start"
          className="max-h-80 w-64"
          side={collapsed ? 'right' : 'bottom'}
        >
          <DropdownMenuLabel>
            {t('businessSwitcher.switchContext')}
          </DropdownMenuLabel>

          {canGoGlobal && (
            <>
              <DropdownMenuItem onSelect={() => setBusiness(null)}>
                <Globe />
                <span className="flex-1">{t('businessSwitcher.global')}</span>
                {!business && <Check className="size-4 opacity-60" />}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          {isError && (
            <p className="text-muted-foreground px-2 py-3 text-xs">
              {t('businessSwitcher.loadFailed')}
            </p>
          )}

          {!isError && options.length === 0 && (
            <p className="text-muted-foreground px-2 py-3 text-xs">
              {t('businessSwitcher.noneAssigned')}
            </p>
          )}

          {options.map((option) => (
            <DropdownMenuItem
              key={option.id}
              onSelect={() => setBusiness(option)}
            >
              <Store />
              <span className="flex-1 truncate">{option.name}</span>
              {business?.id === option.id && (
                <Check className="size-4 opacity-60" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
